import { useCallback, useEffect, useMemo, useState } from 'react';
import { UnifiedTable } from '../../../../components/UnifiedTable';
import Modal from '../../../../components/Modal';
import OzonFbsShipment from './OzonFbsShipment';
import { useApi } from '../../../../hooks/useApi';
import { useUrlParams } from '../../../../hooks/useUrlParams';
import logger from '../../../../utils/logger';
import {
    printChestnyZnakLabels,
    openBase64Pdf,
    groupPostingsForLabelPrint,
} from './ozonLabelPrint';
import CustomerChatModal from '../shared/CustomerChatModal';
import CustomerOrdersModal from '../shared/CustomerOrdersModal';

// Сборка (упаковка) выполняется на предыдущей стадии, ещё до «Ожидают отгрузки».
const ASSEMBLY_STATUS = 'awaiting_packaging';
// На «Ожидают отгрузки» отправление уже собрано — здесь только маркировка ЧЗ и печать этикеток.
const MARKING_STATUS = 'awaiting_deliver';

const STATUS_TABS = [
    { key: 'awaiting_packaging', label: 'Ожидают сборки' },
    { key: 'awaiting_deliver', label: 'Ожидают отгрузки' },
    { key: 'delivering', label: 'Доставляются' },
    // Временно скрыт по просьбе — статус driver_pickup сейчас не заполняется синком,
    // вкладка выглядела как всегда пустая. Backend/фильтр не трогаем, только UI.
    // { key: 'driver_pickup', label: 'У курьера', title: '...' },
    { key: 'delivered', label: 'Доставлены' },
    { key: 'cancelled', label: 'Отменены' },
    { key: '', label: 'Все' },
];

// Русскоязычные подписи статусов Ozon (значения приходят от API как есть).
const STATUS_LABEL_RU = {
    awaiting_registration: 'Ожидает регистрации',
    acceptance_in_progress: 'Идёт приёмка',
    awaiting_approve: 'Ожидает подтверждения',
    awaiting_packaging: 'Ожидает сборки',
    awaiting_deliver: 'Ожидает отгрузки',
    arbitration: 'Арбитраж',
    client_arbitration: 'Арбитраж с клиентом',
    delivering: 'Доставляется',
    driver_pickup: 'У курьера',
    delivered: 'Доставлено',
    cancelled: 'Отменено',
    not_accepted: 'Не принято на складе',
};

const STATUS_COLOR = {
    awaiting_packaging: 'var(--status-warn-fg)',
    awaiting_deliver: 'var(--status-info-fg)',
    delivering: 'var(--status-violet-fg)',
    driver_pickup: 'var(--status-teal-fg)',
    delivered: 'var(--status-ok-fg)',
    cancelled: 'var(--status-neutral-fg)',
};

const PAGE_SIZE = 50;

function formatDt(v) {
    if (!v) return '—';
    const d = new Date(v);
    return isNaN(d) ? String(v) : d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function isUrgent(v) {
    if (!v) return false;
    const diff = new Date(v) - Date.now();
    return diff > 0 && diff < 24 * 3600 * 1000;
}

function itemsText(items) {
    if (!Array.isArray(items) || !items.length) return '—';
    return items.map(i => `${i.quantity}× ${i.offer_id || i.name || i.sku || '?'}`).join(', ');
}

const CUSTOMER_BADGE = {
    repeat: { background: '#dcfce7', color: '#166534' },
    single: { background: '#f3f4f6', color: '#4b5563' },
    unknown: { background: '#e5e7eb', color: '#6b7280' },
};

function customerBadge(rank) {
    if (!rank || !rank.history_synced) {
        return { kind: 'unknown', text: 'Нет данных' };
    }
    if (rank.total_orders > 1) {
        return { kind: 'repeat', text: `Постоянный (${rank.total_orders}-я покупка)` };
    }
    return { kind: 'single', text: 'Новый клиент' };
}

function OzonFbsOrders({ accountId }) {
    const { request, showStatus } = useApi();
    // Ссылка из уведомления («заказ постоянного клиента») ведёт на конкретное отправление: ?sub=posting:<номер>.
    const { get: getUrlParam, update: updateUrlParams } = useUrlParams();
    const urlSub = getUrlParam('sub') || '';
    const focusedPosting = urlSub.startsWith('posting:') ? urlSub.slice('posting:'.length) : '';
    const [status, setStatus] = useState('awaiting_packaging');
    const [postings, setPostings] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);
    const [syncing, setSyncing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [customerRanks, setCustomerRanks] = useState({});
    const [itemsModal, setItemsModal] = useState(null); // { postingNumber, loading, items }
    const [packModal, setPackModal] = useState(null); // { postingNumber, mode: 'assembly' | 'marking' }
    const [labelBusy, setLabelBusy] = useState(null); // posting_number, пока печатаются этикетки заказа
    const [bulkPrinting, setBulkPrinting] = useState(false);
    const [chatModal, setChatModal] = useState(null); // { postingNumber, customerId }
    const [ordersModal, setOrdersModal] = useState(null); // customerId

    const load = useCallback(async (s, p, ps) => {
        setLoading(true);
        try {
            const statusParam = focusedPosting
                ? `&posting_number=${encodeURIComponent(focusedPosting)}`
                : (s ? `&status=${encodeURIComponent(s)}` : '');
            const data = await request(`/api/v1/ozon-ops/${accountId}/fbs/postings?page=${p}&page_size=${ps}${statusParam}`);
            const items = Array.isArray(data?.items) ? data.items : [];
            setPostings(items);
            setTotal(Number(data?.total || 0));

            const customerIds = [...new Set(items.map(i => i.customer_id).filter(Boolean))];
            if (customerIds.length > 0) {
                const query = customerIds.map(id => `customer_id=${encodeURIComponent(id)}`).join('&');
                try {
                    const ranks = await request(`/api/v1/ozon-ops/${accountId}/fbs-postings/customer-ranks?${query}`);
                    setCustomerRanks(ranks || {});
                } catch (e) {
                    logger.error(e);
                    setCustomerRanks({});
                }
            } else {
                setCustomerRanks({});
            }
        } catch (e) {
            logger.error(e);
        } finally {
            setLoading(false);
        }
    }, [accountId, request, focusedPosting]);

    useEffect(() => {
        if (!accountId) return;
        load(status, 1, pageSize);
        setPage(1);
        // eslint-disable-next-line
    }, [accountId, status, focusedPosting]);

    const handleStatusChange = (s) => {
        setStatus(s);
    };

    const openItemsModal = useCallback(async (postingNumber) => {
        setItemsModal({ postingNumber, loading: true, items: [] });
        try {
            const data = await request(`/api/v1/ozon-ops/${accountId}/fbs/postings/${encodeURIComponent(postingNumber)}/items`);
            setItemsModal({ postingNumber, loading: false, items: Array.isArray(data) ? data : [] });
        } catch (e) {
            logger.error(e);
            showStatus('error', e?.message || 'Ошибка загрузки товаров.');
            setItemsModal(null);
        }
    }, [accountId, request, showStatus]);

    const handleSync = async () => {
        setSyncing(true);
        try {
            const res = await request(`/api/v1/ozon-ops/${accountId}/fbs/postings/sync`, { method: 'POST' });
            showStatus('success', `Синхронизировано: +${res?.created ?? 0} новых, обновлено ${res?.updated ?? 0}.`);
            await load(status, 1, pageSize);
            setPage(1);
        } catch (e) {
            logger.error(e);
            showStatus('error', e?.message || 'Ошибка синхронизации заказов.');
        } finally {
            setSyncing(false);
        }
    };

    // Этикетка одного заказа: QR-лейбл поставки Ozon (уже содержит ШК товаров).
    const handlePrintLabels = useCallback(async (postingNumber) => {
        setLabelBusy(postingNumber);
        try {
            const label = await request(`/api/v1/ozon-ops/${accountId}/fbs/postings/${encodeURIComponent(postingNumber)}/label`);
            openBase64Pdf(label?.file_content, label?.file_name);
        } catch (e) {
            logger.error(e);
            showStatus('error', e?.message || 'Ошибка печати этикеток.');
        } finally {
            setLabelBusy(null);
        }
    }, [accountId, request, showStatus]);

    // Собрать все отправления «Ожидают отгрузки» (не только текущую страницу таблицы).
    const fetchAllForPrint = useCallback(async () => {
        const collected = [];
        let p = 1;
        const ps = 200;
        // Безопасный предел страниц — не уйти в бесконечный цикл при неожиданно большом total.
        for (let guard = 0; guard < 25; guard += 1) {
            const data = await request(`/api/v1/ozon-ops/${accountId}/fbs/postings?page=${p}&page_size=${ps}&status=${encodeURIComponent(MARKING_STATUS)}`);
            const items = Array.isArray(data?.items) ? data.items : [];
            collected.push(...items);
            const total = Number(data?.total || 0);
            if (collected.length >= total || items.length === 0) break;
            p += 1;
        }
        return collected;
    }, [accountId, request]);

    const handlePrintAllLabels = useCallback(async () => {
        setBulkPrinting(true);
        try {
            const all = await fetchAllForPrint();
            const ordered = groupPostingsForLabelPrint(all);
            if (ordered.length === 0) {
                showStatus('info', 'Нет отправлений для печати на стадии «Ожидают отгрузки».');
                return;
            }
            for (const posting of ordered) {
                const postingNumber = posting.posting_number;
                // eslint-disable-next-line no-await-in-loop
                const shipment = await request(`/api/v1/ozon-ops/${accountId}/fbs/postings/${encodeURIComponent(postingNumber)}/shipment`);
                const marks = (shipment?.items || []).flatMap(entry => (entry.exemplars || []).map(ex => ex.mark)).filter(Boolean);
                if (marks.length > 0) printChestnyZnakLabels(marks);

                // eslint-disable-next-line no-await-in-loop
                const label = await request(`/api/v1/ozon-ops/${accountId}/fbs/postings/${encodeURIComponent(postingNumber)}/label`);
                openBase64Pdf(label?.file_content, label?.file_name);

                // Небольшая пауза между заказами — иначе браузер блокирует поток popup-окон.
                // eslint-disable-next-line no-await-in-loop
                await new Promise(resolve => setTimeout(resolve, 450));
            }
            showStatus('success', `Печать этикеток запущена для ${ordered.length} отправлений.`);
        } catch (e) {
            logger.error(e);
            showStatus('error', e?.message || 'Ошибка массовой печати этикеток.');
        } finally {
            setBulkPrinting(false);
        }
    }, [accountId, request, showStatus, fetchAllForPrint]);

    const tableData = useMemo(() => postings.map(p => ({
        ...p,
        items_text: itemsText(p.items),
        warehouse_text: p.warehouse_name || p.warehouse_id || '—',
        delivery_text: p.delivery_method_name || p.tpl_integration_type || '—',
    })), [postings]);

    const columns = useMemo(() => [
        {
            key: 'posting_number', header: 'Номер отправления', width: 200, type: 'custom', sticky: true, sortable: false,
            render: (v, row) => (
                <span>
                    <span style={{ fontFamily: 'monospace', fontSize: 13 }}>{v}</span>
                    {row.is_multibox && (
                        <span style={{ marginLeft: 6, fontSize: 12, background: '#e0e7ff', color: '#3730a3', borderRadius: 4, padding: '1px 5px' }}>
                            ×{row.multi_box_qty || '?'} кор.
                        </span>
                    )}
                </span>
            ),
        },
        {
            key: 'customer_id', header: 'Клиент', width: 210, type: 'custom', sortable: false,
            render: (v, row) => {
                if (!v) return '—';
                const badge = customerBadge(customerRanks[v]);
                const style = CUSTOMER_BADGE[badge.kind];
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
                        <span style={{ fontFamily: 'monospace' }}>{v}</span>
                        <span style={{ background: style.background, color: style.color, borderRadius: 4, padding: '1px 6px', fontSize: 12, width: 'fit-content' }}>
                            {badge.text}
                        </span>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
                            <button
                                type="button"
                                className="btn btn--sm"
                                onClick={() => setChatModal({ postingNumber: row.posting_number, customerId: v })}
                                title="Написать покупателю напрямую, без перехода в Повторные заказы"
                                style={{ fontSize: 12, padding: '2px 8px' }}
                            >
                                💬 Написать
                            </button>
                            <button
                                type="button"
                                className="btn btn--sm"
                                onClick={() => setOrdersModal(v)}
                                title="Показать историю заказов покупателя, без перехода в Повторные заказы"
                                style={{ fontSize: 12, padding: '2px 8px' }}
                            >
                                🔍 Что покупал
                            </button>
                        </div>
                    </div>
                );
            },
        },
        {
            key: 'status', header: 'Статус', width: 160, type: 'custom', sortable: false,
            render: (v, row) => (
                <span>
                    <span style={{ color: STATUS_COLOR[v] || 'var(--status-neutral-fg)', fontWeight: 500, fontSize: 13 }}>{STATUS_LABEL_RU[v] || v || '—'}</span>
                    {row.substatus === 'ship_failed' && (
                        <span style={{ marginLeft: 6, fontSize: 12, background: '#fee2e2', color: '#dc2626', borderRadius: 4, padding: '1px 5px' }}>
                            сборка не удалась
                        </span>
                    )}
                </span>
            ),
        },
        { key: 'in_process_at', header: 'Принят', width: 140, type: 'text', sortable: false, render: (v) => formatDt(v) },
        {
            key: 'shipment_date', header: 'Дата отгрузки', width: 140, type: 'custom', sortable: false,
            render: (v) => (
                <span style={{ fontSize: 13, color: isUrgent(v) ? 'var(--text-danger)' : 'inherit', fontWeight: isUrgent(v) ? 600 : 400 }}>
                    {formatDt(v)}
                </span>
            ),
        },
        { key: 'warehouse_text', header: 'Склад', width: 200, type: 'text', sortable: false },
        {
            key: 'items_text', header: 'Товары', width: 300, type: 'custom', sortable: false,
            render: (v, row) => (
                <button
                    type="button"
                    onClick={() => openItemsModal(row.posting_number)}
                    title="Открыть список товаров"
                    style={{
                        background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                        textAlign: 'left', fontSize: 13, color: 'var(--text-link)', textDecoration: 'underline',
                    }}
                >
                    {v}
                </button>
            ),
        },
        { key: 'delivery_text', header: 'Метод доставки', width: 200, type: 'text', sortable: false },
        {
            key: 'packaging', header: 'Сборка / ЧЗ / Этикетки', width: 230, type: 'custom', sortable: false,
            render: (_v, row) => {
                if (row.status === ASSEMBLY_STATUS) {
                    return (
                        <button
                            type="button"
                            onClick={() => setPackModal({ postingNumber: row.posting_number, mode: 'assembly' })}
                        >
                            Собрать
                        </button>
                    );
                }
                if (row.status === MARKING_STATUS) {
                    return (
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <button
                                type="button"
                                onClick={() => setPackModal({ postingNumber: row.posting_number, mode: 'marking' })}
                            >
                                Маркировка ЧЗ
                            </button>
                            <button
                                type="button"
                                disabled={labelBusy === row.posting_number}
                                onClick={() => handlePrintLabels(row.posting_number)}
                            >
                                {labelBusy === row.posting_number ? 'Печать…' : 'Этикетки'}
                            </button>
                        </div>
                    );
                }
                return <span className="muted" style={{ fontSize: 12 }}>—</span>;
            },
        },
    ], [customerRanks, openItemsModal, labelBusy, handlePrintLabels]);

    const toolbarActions = useMemo(() => {
        const actions = [];
        if (status === MARKING_STATUS) {
            actions.push({
                label: bulkPrinting ? 'Печать этикеток…' : 'Распечатать все этикетки',
                onClick: handlePrintAllLabels,
                disabled: bulkPrinting,
            });
        }
        return actions;
        // eslint-disable-next-line
    }, [status, bulkPrinting]);

    const itemsModalColumns = useMemo(() => [
        { key: 'offer_id', header: 'Артикул', width: 160, type: 'text', sortable: false },
        {
            key: 'sku', header: 'SKU', width: 130, type: 'custom', sortable: false,
            render: (v, row) => (
                row.sku_raw
                    ? (
                        <a href={`https://www.ozon.ru/product/${row.sku_raw}/`} target="_blank" rel="noreferrer">
                            {v}
                        </a>
                    )
                    : v
            ),
        },
        { key: 'barcode', header: 'ШК', width: 170, type: 'text', sortable: false },
        { key: 'name', header: 'Название', width: 420, type: 'text', sortable: false },
        { key: 'quantity', header: 'Количество', width: 120, type: 'text', sortable: false },
    ], []);

    const itemsModalData = useMemo(() => (itemsModal?.items || []).map(i => ({
        ...i,
        offer_id: i.offer_id || '—',
        sku_raw: i.sku || '',
        sku: i.sku || '—',
        barcode: i.barcode || '—',
        name: i.name || '—',
    })), [itemsModal]);

    return (
        <div className="stack-12">
            <div className="account-tabs account-tabs--secondary" style={{ marginBottom: 0, alignItems: 'center' }}>
                {STATUS_TABS.map(t => (
                    <span key={t.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <button
                            type="button"
                            title={t.title}
                            className={status === t.key ? 'active' : ''}
                            onClick={() => handleStatusChange(t.key)}
                        >
                            {t.label}
                        </button>
                        {t.key === '' && (
                            <button
                                type="button"
                                className="primary"
                                onClick={handleSync}
                                disabled={syncing}
                                title="Синхронизировать заказы FBS с Ozon"
                            >
                                {syncing ? 'Синхронизация…' : 'Синхронизировать'}
                            </button>
                        )}
                    </span>
                ))}
            </div>

            {focusedPosting && (
                <div className="fbs-focus" role="status" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', margin: '0 0 10px', padding: '10px 14px', border: '1px solid var(--surface-border)', borderRadius: 8, background: 'var(--surface)', color: 'var(--ink)' }}>
                    <span>Показан заказ <strong>{focusedPosting}</strong> из уведомления. Статус покупателя — в столбце «Клиент».</span>
                    <button type="button" className="ghost" onClick={() => updateUrlParams({ sub: '' })}>Показать все заказы</button>
                </div>
            )}

            <UnifiedTable
                tableId="ozon-fbs-orders"
                columns={columns}
                data={tableData}
                totalRows={total}
                page={page}
                pageSize={pageSize}
                onPageChange={nextPage => { setPage(nextPage); load(status, nextPage, pageSize); }}
                onPageSizeChange={nextSize => { setPageSize(nextSize); setPage(1); load(status, 1, nextSize); }}
                loading={loading}
                toolbarActions={toolbarActions}
                onExportCsv={true}
                onCopyTable={true}
                getRowKey={(row) => row.id || row.posting_number}
            />

            <Modal
                open={!!itemsModal}
                onClose={() => setItemsModal(null)}
                title={`Товары в отправлении ${itemsModal?.postingNumber || ''}`}
                wide
                panelStyle={{ width: '96vw', maxWidth: 'none' }}
            >
                <UnifiedTable
                    tableId="ozon-fbs-order-items"
                    columns={itemsModalColumns}
                    data={itemsModalData}
                    totalRows={itemsModalData.length}
                    loading={!!itemsModal?.loading}
                    showPagination={false}
                    onExportCsv={false}
                    onCopyTable={true}
                    getRowKey={(row) => row.id}
                />
            </Modal>

            <Modal
                open={!!packModal}
                onClose={() => { setPackModal(null); load(status, page, pageSize); }}
                title={
                    packModal?.mode === 'assembly'
                        ? `Сборка отправления — ${packModal?.postingNumber || ''}`
                        : `Маркировка ЧЗ — ${packModal?.postingNumber || ''}`
                }
                wide
            >
                {packModal && (
                    <OzonFbsShipment
                        accountId={accountId}
                        initialPostingNumber={packModal.postingNumber}
                        mode={packModal.mode}
                    />
                )}
            </Modal>

            {chatModal && (
                <CustomerChatModal
                    request={request}
                    accountId={accountId}
                    postingNumber={chatModal.postingNumber}
                    customerId={chatModal.customerId}
                    onClose={() => setChatModal(null)}
                />
            )}

            {ordersModal && (
                <CustomerOrdersModal
                    request={request}
                    accountId={accountId}
                    customerId={ordersModal}
                    onClose={() => setOrdersModal(null)}
                />
            )}
        </div>
    );
}

export default OzonFbsOrders;
